import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getFirestore, collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from "firebase/firestore";
import app from "./firebase";
import styles from "./StoresDashboard.module.css";

const db = getFirestore(app);

export default function StoresDashboard() {
  const location = useLocation();
  const [stores, setStores] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editStore, setEditStore] = useState(null);
  const [form, setForm] = useState({ name: "", location: "", contact: "" });
  const [highlightedId, setHighlightedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [storeSnap, ingSnap] = await Promise.all([
        getDocs(collection(db, "stores")),
        getDocs(collection(db, "ingredients")),
      ]);
      setStores(storeSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIngredients(ingSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Handle highlighting from spotlight search navigation - triggers on any highlight event
  useEffect(() => {
    if (location.state?.highlightId && !loading && stores.length > 0) {
      setHighlightedId(location.state.highlightId);
      
      const scrollToElement = () => {
        const element = document.getElementById(`store-${location.state.highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return true;
        }
        return false;
      };

      // Try immediately first
      if (!scrollToElement()) {
        // If element not found, wait a bit for DOM to update
        const timer = setTimeout(() => {
          scrollToElement();
        }, 100);
        setTimeout(() => setHighlightedId(null), 3000);
        return () => clearTimeout(timer);
      } else {
        setTimeout(() => setHighlightedId(null), 3000);
      }
    }
  }, [location.state, loading, stores]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    await addDoc(collection(db, "stores"), { 
      name: form.name,
      location: form.location || "",
      contact: form.contact || "",
      createdAt: new Date()
    });
    setForm({ name: "", location: "", contact: "" });
    setShowAdd(false);
    fetchData();
  };

  const handleDelete = async (storeId) => {
    // Check if store has ingredients
    const storeIngredients = ingredients.filter(i => i.storeId === storeId);
    if (storeIngredients.length > 0) {
      if (!window.confirm(`This store has ${storeIngredients.length} ingredient(s). Deleting will remove these associations. Continue?`)) {
        return;
      }
    }
    await deleteDoc(doc(db, "stores", storeId));
    fetchData();
  };

  const handleEdit = (store) => {
    setEditStore(store);
    setForm({ 
      name: store.name, 
      location: store.location || "",
      contact: store.contact || ""
    });
    setShowEdit(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    await updateDoc(doc(db, "stores", editStore.id), {
      name: form.name,
      location: form.location || "",
      contact: form.contact || "",
      lastUpdated: new Date()
    });
    setForm({ name: "", location: "", contact: "" });
    setShowEdit(false);
    setEditStore(null);
    fetchData();
  };

  // Filter stores by search term
  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (store.location && store.location.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.header}>
        <h2 className={styles.dashboardTitle}>Stores Management</h2>
        <button
          className={styles.addButton}
          onClick={() => setShowAdd(true)}
        >
          + Add Store
        </button>
      </div>

      {/* Stats Section */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{stores.length}</div>
          <div className={styles.statLabel}>Total Stores</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {stores.filter(store => 
              ingredients.some(ingredient => ingredient.storeId === store.id)
            ).length}
          </div>
          <div className={styles.statLabel}>Active Stores</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{ingredients.length}</div>
          <div className={styles.statLabel}>Total Ingredients</div>
        </div>
      </div>

      {/* Search */}
      <div className={styles.searchContainer}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search stores by name or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            className={styles.clearSearch}
            onClick={() => setSearchTerm("")}
            type="button"
          >
            ✕
          </button>
        )}
      </div>

      {(showAdd || showEdit) && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>{showEdit ? "Edit Store" : "Add Store"}</h3>
            <form onSubmit={showEdit ? handleEditSubmit : handleSubmit}>
              <div className={styles.formGroup}>
                <label>Store Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Local Grocery Store"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>Location</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="e.g., 123 Main Street, City"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Contact Info</label>
                <input
                  type="text"
                  value={form.contact}
                  onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  placeholder="e.g., (555) 123-4567 or manager@store.com"
                />
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAdd(false);
                    setShowEdit(false);
                    setForm({ name: "", location: "", contact: "" });
                    setEditStore(null);
                  }}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.saveButton}>
                  {showEdit ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={styles.cardGrid}>
        {filteredStores.map(store => {
          const storeIngredients = ingredients.filter(i => i.storeId === store.id);
          return (
            <div 
              key={store.id} 
              id={`store-${store.id}`}
              className={`${styles.storeCard} ${
                highlightedId === store.id ? styles.highlighted : ''
              }`}
            >
              <div className={styles.storeHeader}>
                <div className={styles.storeIcon}>🏪</div>
                <div className={styles.storeName}>{store.name}</div>
              </div>
              
              {store.location && (
                <div className={styles.storeLocation}>
                  📍 {store.location}
                </div>
              )}
              
              {store.contact && (
                <div className={styles.storeContact}>
                  📞 {store.contact}
                </div>
              )}

              <div className={styles.ingredientsSection}>
                <div className={styles.ingredientsHeader}>
                  <span className={styles.ingredientsCount}>
                    {storeIngredients.length} ingredient{storeIngredients.length !== 1 ? 's' : ''}
                  </span>
                </div>
                
                {storeIngredients.length > 0 ? (
                  <div className={styles.ingredientsList}>
                    {storeIngredients.slice(0, 3).map(ingredient => (
                      <span key={ingredient.id} className={styles.ingredientTag}>
                        {ingredient.name}
                      </span>
                    ))}
                    {storeIngredients.length > 3 && (
                      <span className={styles.moreIngredients}>
                        +{storeIngredients.length - 3} more
                      </span>
                    )}
                  </div>
                ) : (
                  <div className={styles.noIngredients}>
                    No ingredients assigned
                  </div>
                )}
              </div>

              <div className={styles.storeActions}>
                <button
                  className={styles.editButton}
                  onClick={() => handleEdit(store)}
                >
                  Edit
                </button>
                <button
                  className={styles.deleteButton}
                  onClick={() => handleDelete(store.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredStores.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🏪</div>
          <div className={styles.emptyText}>No stores found</div>
          <div className={styles.emptySubtext}>
            {searchTerm 
              ? "Try adjusting your search term" 
              : "Add your first store to get started"}
          </div>
        </div>
      )}
    </div>
  );
}